/**
 * AkteBlickPage - Akte auf einen Blick
 * Vollständige Übersichtsseite eines Betreuten - alles Relevante auf einer Seite ohne Tabs.
 * Mit KI-Zusammenfassung und PDF-Export.
 * ALLE KACHELN SIND KLICKBAR und öffnen neue Tabs (keine Modals).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import {
  ArrowLeft,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Euro,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building2,
  UserCircle,
  FileText,
  Download,
  Printer,
  RefreshCw,
  Sparkles,
  Heart,
  Briefcase,
  Scale,
  Home,
  CreditCard,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Plus,
  ClipboardList,
  Pencil,
  Save,
  X as XIcon,
  Loader2,
  ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { gruppiereAufgaben, fortschritt } from '@/lib/aufgabenBaum';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatDatum, formatWohnform } from '@/lib/formatUtils';
import StammdatenEditModal from '@/components/StammdatenEditModal';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Status-Farben
const STATUS_COLORS = {
  aktiv: 'bg-green-100 text-green-700 border-green-200',
  ruhend: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  uebergabe: 'bg-orange-100 text-orange-700 border-orange-200',
  beendet: 'bg-slate-100 text-slate-700 border-slate-200',
};

const PRIORITAET_COLORS = {
  niedrig: 'bg-slate-100 text-slate-600',
  mittel: 'bg-blue-100 text-blue-700',
  hoch: 'bg-orange-100 text-orange-700',
  dringend: 'bg-red-100 text-red-700',
};

const LEISTUNG_ICONS = {
  rente: Euro,
  alg1: Briefcase,
  alg2: Briefcase,
  grundsicherung: Euro,
  pflegegeld: Heart,
  wohngeld: Home,
  krankenversicherung: Heart,
  pflegeversicherung: Heart,
};

export default function AkteBlickPage() {
  const { id } = useTabParams();
  const navigate = useNavigate();
  const { openTab, activeTabId, updateTabTitle } = useTabs();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [zusammenfassung, setZusammenfassung] = useState(null);
  const [zusammenfassungLoading, setZusammenfassungLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stammdatenEditOpen, setStammdatenEditOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadData();
      loadZusammenfassung();
      // Tracking: Als zuletzt angesehen markieren
      fetch(`${API_URL}/api/zuletzt-angesehen/${id}`, { method: 'POST' })
        .catch(err => console.error('Zuletzt angesehen tracking Fehler:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Tab-Titel mit Klient-Name aktualisieren
  useEffect(() => {
    if (data?.stammdaten && activeTabId) {
      const name = `${data.stammdaten.vorname || ''} ${data.stammdaten.nachname || ''}`.trim();
      if (name) updateTabTitle(activeTabId, `Blick: ${name}`);
    }
  }, [data, activeTabId, updateTabTitle]);

  // Navigation zu Maßnahme/Leistung
  const openMassnahme = (massnahme) => {
    openTab(`/akten/${id}?tab=massnahmen`, massnahme.bezeichnung || massnahme.typ);
  };

  // Navigation zu Aufgabe
  const openAufgabe = (aufgabe) => {
    openTab(`/aufgaben/${aufgabe.id}`, aufgabe.titel);
  };

  // Navigation zu Frist
  const openFrist = (frist) => {
    openTab(`/fristen?frist=${frist.id}`, frist.bezeichnung || frist.typ);
  };

  // Navigation zu Schulden/Gläubiger
  const openSchulden = (schuld) => {
    openTab(`/schulden-dashboard?klient=${id}&schuld=${schuld.id}`, schuld.glaeubiger);
  };

  // Navigation zu Finanzen
  const openFinanzen = () => {
    openTab(`/buchhaltung?klient=${id}`, 'Buchhaltung');
  };

  // Navigation zu Institution
  const openInstitution = (inst) => {
    openTab(`/personen/juristisch/${inst.id}`, inst.name);
  };

  // Navigation zu Person
  const openPerson = (person) => {
    openTab(`/personen/natuerlich/${person.id}`, person.name);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/akte-blick/${id}`);
      if (!res.ok) throw new Error('Fehler beim Laden');
      const result = await res.json();
      setData(result);
    } catch (error) {
      toast.error('Fehler beim Laden der Akte');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadZusammenfassung = async () => {
    setZusammenfassungLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/akte-blick/${id}/zusammenfassung`);
      const result = await res.json();
      setZusammenfassung(result);
    } catch (error) {
      console.error('KI-Zusammenfassung Fehler:', error);
      setZusammenfassung({ zusammenfassung: 'Zusammenfassung konnte nicht geladen werden.', quelle: 'error' });
    } finally {
      setZusammenfassungLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const betreuer = user?.name || 'Betreuer';
      const res = await fetch(`${API_URL}/api/akte-blick/${id}/pdf?betreuer_name=${encodeURIComponent(betreuer)}&mit_zusammenfassung=true`);
      if (!res.ok) throw new Error('PDF-Generierung fehlgeschlagen');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Akte_Blick_${data?.stammdaten?.vorname}_${data?.stammdaten?.nachname}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('PDF-Export fehlgeschlagen');
      console.error(error);
    } finally {
      setPdfLoading(false);
    }
  };

  const printPage = () => {
    window.print();
  };

  const goBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="akte-blick-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-48 lg:col-span-2" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-slate-500">Akte nicht gefunden</p>
        <Button variant="outline" onClick={goBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
      </div>
    );
  }

  const { stammdaten, leistungen, betreuung_leistungen, einnahmen_vorgaenge, betreuung_konten, aufgaben, fristen, schulden, schulden_gesamt, institutionen, personen, dokumente, korrespondenz, verguetung, finanzen } = data;
  // Gleicher Bestand + gleiche Zaehlung wie die Betreuungsakte (02.11.2026): Unteraufgaben unter ihrer Hauptaufgabe, nicht einzeln gezaehlt
  const aufgabenBaum = gruppiereAufgaben(aufgaben || []);
  const unteraufgabenAnzahl = (aufgaben?.length || 0) - aufgabenBaum.length;
  const euro = (wert, erfasst) => (erfasst === false || typeof wert !== 'number') ? null : `${wert.toFixed(2)} €`;

  return (
    <div className="space-y-6 print:space-y-4" data-testid="akte-blick-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Akte auf einen Blick</h1>
            <p className="text-slate-500">{stammdaten.vorname} {stammdaten.nachname}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => openTab('/antragsassistent', { label: `Antrag für ${stammdaten.vorname} ${stammdaten.nachname}`, type: 'antrag', klientId: id })}
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="btn-antrag-ausfuellen"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Antrag ausfüllen
          </Button>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button variant="outline" onClick={printPage}>
            <Printer className="w-4 h-4 mr-2" />
            Drucken
          </Button>
          <Button onClick={downloadPdf} disabled={pdfLoading} data-testid="btn-pdf-export">
            <Download className={`w-4 h-4 mr-2 ${pdfLoading ? 'animate-spin' : ''}`} />
            PDF Export
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center border-b-2 border-blue-600 pb-4 mb-6">
        <h1 className="text-xl font-bold text-blue-800">AKTE AUF EINEN BLICK</h1>
        <p className="text-slate-600">{stammdaten.vorname} {stammdaten.nachname} | Fallnummer: {stammdaten.fallnummer || 'k.A.'}</p>
      </div>

      {/* KI-Zusammenfassung */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white print:break-inside-avoid" data-testid="ki-zusammenfassung">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
            <Sparkles className="w-5 h-5" />
            Was ist bei diesem Klienten gerade wichtig?
          </CardTitle>
        </CardHeader>
        <CardContent>
          {zusammenfassungLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              KI-Zusammenfassung wird generiert...
            </div>
          ) : zusammenfassung ? (
            <div className="space-y-2">
              <p className="text-slate-700 leading-relaxed">{zusammenfassung.zusammenfassung}</p>
              {zusammenfassung.quelle === 'fallback' && (
                <p className="text-xs text-slate-400">(Automatisch generiert)</p>
              )}
            </div>
          ) : (
            <p className="text-slate-500">Zusammenfassung nicht verfügbar</p>
          )}
        </CardContent>
      </Card>

      {/* Stammdaten + Finanzen Übersicht */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:grid-cols-3">
        {/* Stammdaten - Header klickbar → öffnet Betreuungsakte */}
        <Card className="lg:col-span-2 print:col-span-2 print:break-inside-avoid">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Stammdaten
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                  onClick={() => setStammdatenEditOpen(true)}
                  data-testid="stammdaten-edit-btn"
                  title="Stammdaten bearbeiten"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-300 hover:text-blue-500"
                  onClick={() => openTab(`/akten/${id}`, `${stammdaten.vorname} ${stammdaten.nachname}`)}
                  title="Betreuungsakte öffnen"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="text-slate-500 text-xs">Status</label>
                <div className="mt-1">
                  <Badge className={STATUS_COLORS[stammdaten.status] || 'bg-slate-100'}>
                    {stammdaten.status}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-slate-500 text-xs">Priorität</label>
                <div className="mt-1">
                  <Badge className={PRIORITAET_COLORS[stammdaten.prioritaet] || 'bg-slate-100'}>
                    {stammdaten.prioritaet}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-slate-500 text-xs">Fallnummer</label>
                <p className="font-medium">{stammdaten.fallnummer || 'k.A.'}</p>
              </div>
              <div>
                <label className="text-slate-500 text-xs">Geburtsdatum</label>
                <p className="font-medium">{formatDatum(stammdaten.geburtsdatum)}</p>
              </div>
              <div
                className="cursor-pointer hover:bg-blue-50 rounded-lg p-1 -m-1 transition-colors group"
                onClick={() => openTab(`/akten/${id}`, 'Lebenssituation')}
                data-testid="stamm-pflegegrad-link"
              >
                <label className="text-slate-500 text-xs group-hover:text-blue-600">Pflegegrad</label>
                <p className="font-medium group-hover:text-blue-700 flex items-center gap-1">
                  {stammdaten.pflegegrad || 'keiner'}
                  <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />
                </p>
              </div>
              <div
                className="cursor-pointer hover:bg-blue-50 rounded-lg p-1 -m-1 transition-colors group"
                onClick={() => openTab(`/akten/${id}`, 'Lebenssituation')}
                data-testid="stamm-wohnform-link"
              >
                <label className="text-slate-500 text-xs group-hover:text-blue-600">Wohnform</label>
                <p className="font-medium group-hover:text-blue-700 flex items-center gap-1">
                  {formatWohnform(stammdaten.wohnform)}
                  <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />
                </p>
              </div>
              <div
                className={`rounded-lg p-1 -m-1 transition-colors group ${
                  stammdaten.krankenversicherung_id 
                    ? 'cursor-pointer hover:bg-teal-50' 
                    : stammdaten.krankenversicherung 
                      ? 'cursor-pointer hover:bg-blue-50' 
                      : ''
                }`}
                onClick={() => {
                  if (stammdaten.krankenversicherung_id) {
                    openTab(`/netzwerk/institution/${stammdaten.krankenversicherung_id}`, stammdaten.krankenversicherung);
                  } else if (stammdaten.krankenversicherung) {
                    openTab(`/akten/${id}`, 'Lebenssituation');
                  }
                }}
                data-testid="stamm-kv-link"
              >
                <label className="text-slate-500 text-xs group-hover:text-blue-600">Krankenversicherung</label>
                <p className={`font-medium flex items-center gap-1 ${stammdaten.krankenversicherung ? 'group-hover:text-blue-700' : ''}`}>
                  {stammdaten.krankenversicherung || 'Nicht erfasst'}
                  {stammdaten.krankenversicherung && <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />}
                </p>
              </div>
              <div>
                <label className="text-slate-500 text-xs">Haupteinnahmequelle</label>
                <p className="font-medium">{stammdaten.haupteinnahmequelle || (finanzen?.einnahmen?.length > 0 ? finanzen.einnahmen[0]?.bezeichnung || finanzen.einnahmen[0]?.typ : 'Nicht erfasst')}</p>
              </div>
              <div className="col-span-2">
                <label className="text-slate-500 text-xs">Adresse</label>
                <p className="font-medium flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {stammdaten.adresse || ''} {stammdaten.plz || ''} {stammdaten.ort || 'k.A.'}
                </p>
              </div>
              <div>
                <label className="text-slate-500 text-xs">Telefon</label>
                <p className="font-medium flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {stammdaten.telefon || 'k.A.'}
                </p>
              </div>
              <div className="col-span-2 md:col-span-3">
                <label className="text-slate-500 text-xs">Aufgabenkreise</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {stammdaten.aufgabenkreise?.length > 0 ? (
                    stammdaten.aufgabenkreise.map((ak, i) => (
                      <Badge key={i} variant="secondary" className="text-xs whitespace-normal text-left" data-testid={`blick-aufgabenkreis-${i}`}>{ak}</Badge>
                    ))
                  ) : (
                    <span className="text-slate-400" data-testid="blick-aufgabenkreise-leer">nicht erfasst</span>
                  )}
                  {stammdaten.aufgabenkreise_quelle === 'nur_kategorie' && (
                    <span className="text-xs text-amber-700 w-full" data-testid="blick-aufgabenkreise-nur-kategorie">Nur Kategorien – gerichtlicher Wortlaut noch nicht erfasst</span>
                  )}
                </div>
              </div>
              {stammdaten.diagnosen && (
                <div className="col-span-2 md:col-span-3">
                  <label className="text-slate-500 text-xs">Diagnosen</label>
                  <p className="font-medium text-sm">{stammdaten.diagnosen}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Finanzen Übersicht - KLICKBAR → öffnet Buchhaltung Tab */}
        <Card 
          className="print:break-inside-avoid cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={openFinanzen}
          data-testid="finanzen-card"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-green-600" />
                Finanzen
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-violet-500 transition-colors" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <p className="text-xs text-green-700">Monatliche Einnahmen</p>
              <p className={`text-2xl font-bold ${euro(finanzen?.monatliche_einnahmen, finanzen?.einnahmen_erfasst) ? 'text-green-600' : 'text-slate-400'}`} data-testid="blick-einnahmen">{euro(finanzen?.monatliche_einnahmen, finanzen?.einnahmen_erfasst) || 'nicht erfasst'}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
              <p className="text-xs text-red-700">Schulden gesamt</p>
              <p className={`text-2xl font-bold ${euro(schulden_gesamt, finanzen?.schulden_erfasst) ? 'text-red-600' : 'text-slate-400'}`} data-testid="blick-schulden">{euro(schulden_gesamt, finanzen?.schulden_erfasst) || 'nicht erfasst'}</p>
              <p className="text-xs text-red-500">{schulden?.length || 0} Gläubiger</p>
            </div>
            {finanzen?.konten?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium">Konten ({finanzen.konten.length})</p>
                {finanzen.konten.slice(0, 2).map((k, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">{k.bezeichnung}</span>
                    <span className="font-medium">{k.kontostand?.toFixed(2) || '0.00'} €</span>
                  </div>
                ))}
                {finanzen.konten.length > 2 && (
                  <p className="text-xs text-violet-600">+{finanzen.konten.length - 2} weitere Konten</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bankkonten + Einnahmen */}
      {(betreuung_konten?.length > 0 || einnahmen_vorgaenge?.length > 0 || betreuung_leistungen?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
          {/* Bankkonten */}
          <Card className="print:break-inside-avoid" data-testid="bankkonten-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Bankkonten ({betreuung_konten?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {betreuung_konten?.length > 0 ? (
                <div className="space-y-2">
                  {betreuung_konten.map((k, i) => (
                    <div key={i} className="p-3 bg-blue-50 rounded-lg" data-testid={`bankkonto-item-${i}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{k.bezeichnung}</p>
                          <p className="text-xs text-slate-500">{k.bank} {k.iban ? `- ${k.iban}` : ''}</p>
                          <p className="text-xs text-slate-400">{k.kontoart}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{k.saldo?.toFixed(2) || '0.00'} EUR</p>
                          {k.saldo_datum && <p className="text-xs text-slate-400">Stand: {k.saldo_datum}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm text-center py-4">Keine Bankkonten erfasst</p>
              )}
            </CardContent>
          </Card>

          {/* Einnahmen (Single Source of Truth: klient_vorgaenge) */}
          <Card className="print:break-inside-avoid" data-testid="einnahmen-vorgaenge-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Euro className="w-5 h-5 text-amber-600" />
                Einnahmen ({einnahmen_vorgaenge?.length || betreuung_leistungen?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {einnahmen_vorgaenge?.length > 0 ? (
                <div className="space-y-2">
                  {einnahmen_vorgaenge.map((v, i) => (
                    <button
                      key={v.id || i}
                      onClick={() => openTab(`/akten/${id}`, `${stammdaten?.vorname || ''} ${stammdaten?.nachname || ''}`)}
                      className="w-full text-left p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors cursor-pointer"
                      data-testid={`einnahme-vorgang-item-${i}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{v.typ_label || v.bezeichnung}</p>
                          <p className="text-xs text-slate-500">{v.institution || 'k.A.'}</p>
                          {v.neuester_bescheid && (
                            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded w-fit" data-testid={`bescheid-info-${i}`}>
                              <FileText className="w-3 h-3 shrink-0" />
                              <span className="truncate">{v.neuester_bescheid.titel || v.neuester_bescheid.dateiname || 'Bescheid'}</span>
                              {v.neuester_bescheid.created_date && (
                                <span className="text-emerald-500 shrink-0">
                                  ({new Date(v.neuester_bescheid.created_date).toLocaleDateString('de-DE')})
                                </span>
                              )}
                            </div>
                          )}
                          {!v.neuester_bescheid && v.dokumente_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1">
                              <FileText className="w-3 h-3" />
                              {v.dokumente_count} {v.dokumente_count === 1 ? 'Bescheid' : 'Bescheide'}
                            </span>
                          )}
                          {!v.neuester_bescheid && v.dokumente_count === 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-400 mt-1 italic">
                              Kein Bescheid hinterlegt
                            </span>
                          )}
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className="font-bold text-amber-700">{v.betrag?.toFixed(2) || '0.00'} EUR</p>
                          <p className="text-xs text-slate-400">/{v.turnus || 'mtl.'}</p>
                          <Badge variant="outline" className={`text-[10px] mt-1 ${v.status === 'aktiv' ? 'border-green-300 text-green-700' : 'border-slate-300 text-slate-500'}`}>
                            {v.status || 'aktiv'}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : betreuung_leistungen?.length > 0 ? (
                <div className="space-y-2">
                  {betreuung_leistungen.map((l, i) => (
                    <div key={i} className="p-3 bg-rose-50 rounded-lg" data-testid={`sozialleistung-item-${i}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{l.leistungsart}</p>
                          <p className="text-xs text-slate-500">{l.leistungsgeber || 'k.A.'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-rose-600">{l.betrag?.toFixed(2) || '0.00'} EUR</p>
                          <p className="text-xs text-slate-400">/{l.turnus || 'mtl.'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm text-center py-4">Keine Einnahmen erfasst</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leistungen + Aufgaben */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
        {/* Aktive Leistungen - KLICKBAR → öffnet Maßnahmen-Detail Tab */}
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Aktive Leistungen ({leistungen?.length || 0})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leistungen?.length > 0 ? (
              <div className="space-y-3">
                {leistungen.map((l, i) => {
                  const Icon = LEISTUNG_ICONS[l.typ] || Euro;
                  return (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-emerald-50 hover:shadow-md transition-all group"
                      onClick={() => openMassnahme(l)}
                      data-testid={`leistung-item-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200">
                          <Icon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{l.bezeichnung || l.typ}</p>
                          <p className="text-xs text-slate-500">{l.leistungsgeber || 'k.A.'}</p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <p className="font-bold text-emerald-600">{l.betrag?.toFixed(2) || '0.00'} €</p>
                          <p className="text-xs text-slate-400">/{l.rhythmus || 'mtl.'}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <TrendingUp className="w-10 h-10 mx-auto text-slate-200 mb-2" />
                <p className="text-slate-400 text-sm">Keine aktiven Leistungen</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => openTab(`/akten/${id}?tab=massnahmen`, 'Maßnahmen')}
                >
                  <Plus className="w-3 h-3 mr-1" /> Leistung hinzufügen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Laufende Aufgaben - KLICKBAR → öffnet Aufgaben-Detail Tab */}
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              Offene Aufgaben ({aufgabenBaum.length})
            </CardTitle>
            {unteraufgabenAnzahl > 0 && (
              <p className="text-xs text-slate-500" data-testid="blick-unteraufgaben-hinweis">dazu {unteraufgabenAnzahl} Unteraufgaben (unter ihrer Hauptaufgabe)</p>
            )}
          </CardHeader>
          <CardContent>
            {aufgabenBaum.length > 0 ? (
              <div className="space-y-2">
                {aufgabenBaum.slice(0, 8).map(({ aufgabe: a, kinder }, i) => (
                  <div 
                    key={a.id || i} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:shadow-md transition-all group"
                    onClick={() => openAufgabe(a)}
                    data-testid={`aufgabe-item-${i}`}
                    data-aufgabe-id={a.id}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Badge className={PRIORITAET_COLORS[a.prioritaet] || 'bg-slate-100'} variant="secondary">
                        {a.prioritaet?.[0]?.toUpperCase() || 'M'}
                      </Badge>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{a.titel}</p>
                        {kinder.length > 0 && <p className="text-xs text-blue-700">{fortschritt(kinder, a).text}</p>}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2 flex-shrink-0 ml-2">
                      <p className="text-xs text-slate-500">{a.faelligkeit || 'k.A.'}</p>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                    </div>
                  </div>
                ))}
                {aufgabenBaum.length > 8 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-blue-600"
                    onClick={() => openTab(`/akten/${id}?tab=aufgaben`, 'Aufgaben')}
                  >
                    +{aufgabenBaum.length - 8} weitere anzeigen
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="w-10 h-10 mx-auto text-slate-200 mb-2" />
                <p className="text-slate-400 text-sm">Keine offenen Aufgaben</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => openTab(`/akten/${id}?tab=aufgaben`, 'Aufgaben')}
                >
                  <Plus className="w-3 h-3 mr-1" /> Aufgabe hinzufügen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fristen + Schulden */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
        {/* Anstehende Fristen - KLICKBAR → öffnet Fristkalender Tab */}
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              Anstehende Fristen ({fristen?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fristen?.length > 0 ? (
              <div className="space-y-2">
                {fristen.slice(0, 6).map((f, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 hover:shadow-md transition-all group"
                    onClick={() => openFrist(f)}
                    data-testid={`frist-item-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                      <p className="text-sm font-medium">{f.bezeichnung || f.typ}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-orange-300 text-orange-700">
                        {f.faellig_am || f.datum}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500" />
                    </div>
                  </div>
                ))}
                {fristen.length > 6 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-orange-600"
                    onClick={() => openTab('/fristen', 'Fristkalender')}
                  >
                    +{fristen.length - 6} weitere anzeigen
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Clock className="w-10 h-10 mx-auto text-slate-200 mb-2" />
                <p className="text-slate-400 text-sm">Keine anstehenden Fristen</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verbindlichkeiten/Gläubiger - KLICKBAR → öffnet Schulden-Detail Tab */}
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-red-600" />
              Gläubiger-Übersicht ({schulden?.length || 0})
            </CardTitle>
            <CardDescription>Gesamt: {euro(schulden_gesamt, finanzen?.schulden_erfasst) || 'nicht erfasst'}</CardDescription>
          </CardHeader>
          <CardContent>
            {schulden?.length > 0 ? (
              <div className="space-y-2">
                {schulden.slice(0, 6).map((s, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 hover:shadow-md transition-all group"
                    onClick={() => openSchulden(s)}
                    data-testid={`schulden-item-${i}`}
                  >
                    <p className="text-sm font-medium">{s.glaeubiger}</p>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-red-600">{s.schuldenstand?.toFixed(2) || '0.00'} €</p>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-red-500" />
                    </div>
                  </div>
                ))}
                {schulden.length > 6 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-red-600"
                    onClick={() => openTab(`/schulden-dashboard?klient=${id}`, 'Schulden')}
                  >
                    +{schulden.length - 6} weitere anzeigen
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-green-600 text-sm text-center py-4 flex items-center justify-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Keine Schulden
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Beziehungsnetz - KLICKBAR → öffnet entsprechende Detail-Tabs */}
      <Card className="print:break-inside-avoid">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-purple-600" />
              Verbindungen / Beziehungsnetz
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => openTab(`/netzwerk?person=${id}`, 'Netzwerk')}
            >
              <ExternalLink className="w-3 h-3 mr-1" /> Netzwerk öffnen
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Institutionen */}
            <div>
              <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-teal-600" />
                Institutionen ({institutionen?.length || 0})
              </h4>
              {institutionen?.length > 0 ? (
                <div className="space-y-2">
                  {institutionen.slice(0, 6).map((inst, i) => (
                    <div 
                      key={i} 
                      className="p-3 bg-teal-50 rounded-lg cursor-pointer hover:bg-teal-100 hover:shadow-md transition-all group"
                      onClick={() => openInstitution(inst)}
                      data-testid={`institution-item-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{inst.name}</p>
                          <p className="text-xs text-slate-500">{inst.typ} — {inst.beziehung}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500" />
                      </div>
                    </div>
                  ))}
                  {institutionen.length > 6 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-teal-600"
                      onClick={() => openTab(`/netzwerk?person=${id}&typ=institution`, 'Institutionen')}
                    >
                      +{institutionen.length - 6} weitere
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-400 text-sm">Keine Institutionen verknüpft</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => openTab(`/netzwerk?person=${id}`, 'Netzwerk')}>
                    <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                  </Button>
                </div>
              )}
            </div>

            {/* Personen */}
            <div>
              <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-3">
                <UserCircle className="w-4 h-4 text-purple-600" />
                Personen ({personen?.length || 0})
              </h4>
              {personen?.length > 0 ? (
                <div className="space-y-2">
                  {personen.slice(0, 6).map((p, i) => (
                    <div 
                      key={i} 
                      className="p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 hover:shadow-md transition-all group"
                      onClick={() => openPerson(p)}
                      data-testid={`person-item-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.rolle} — {p.beziehung}</p>
                          {p.telefon && <p className="text-xs text-slate-400">{p.telefon}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-purple-500" />
                      </div>
                    </div>
                  ))}
                  {personen.length > 6 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-purple-600"
                      onClick={() => openTab(`/netzwerk?person=${id}&typ=person`, 'Personen')}
                    >
                      +{personen.length - 6} weitere
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-400 text-sm">Keine Personen verknüpft</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => openTab(`/netzwerk?person=${id}`, 'Netzwerk')}>
                    <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dokumente & Korrespondenz + Vergütung */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
        {/* Letzte Dokumente & Post - KLICKBAR */}
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 rounded-t-lg transition-colors group" onClick={() => openTab(`/akten/${id}?tab=dokumente`, 'Dokumente')}>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Letzte Dokumente & Post
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Dokumente */}
              <div>
                <h4 className="font-medium text-xs text-slate-500 mb-2">Dokumente ({dokumente.length})</h4>
                {dokumente.length > 0 ? (
                  <div className="space-y-1">
                    {dokumente.slice(0, 4).map((d, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between text-sm py-2 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors group"
                        onClick={() => openTab(`/akten/${id}?tab=dokumente`, d.titel || d.dateiname)}
                        data-testid={`dokument-item-${i}`}
                      >
                        <span className="truncate flex-1 group-hover:text-blue-700">{d.titel || d.dateiname}</span>
                        <span className="text-xs text-slate-400 ml-2">{d.created_date?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">Keine Dokumente</p>
                )}
              </div>
              
              {/* Korrespondenz */}
              <div>
                <h4 className="font-medium text-xs text-slate-500 mb-2">Korrespondenz ({korrespondenz.length})</h4>
                {korrespondenz.length > 0 ? (
                  <div className="space-y-1">
                    {korrespondenz.slice(0, 4).map((k, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between text-sm py-2 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors group"
                        onClick={() => openTab(`/akten/${id}?tab=korrespondenz`, k.betreff)}
                        data-testid={`korrespondenz-item-${i}`}
                      >
                        <span className="truncate flex-1 group-hover:text-blue-700">{k.betreff}</span>
                        <span className="text-xs text-slate-400 ml-2">{k.datum?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">Keine Korrespondenz</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vergütung - KLICKBAR → öffnet Vergütung Tab */}
        <Card className="print:break-inside-avoid cursor-pointer hover:shadow-md transition-shadow group" onClick={() => openTab(`/verguetung?klient=${id}`, 'Vergütung')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                Vergütung
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 transition-colors" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {verguetung.letzte_abrechnung ? (
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <p className="text-xs text-indigo-700">Letzte Abrechnung</p>
                  <p className="font-medium">
                    {verguetung.letzte_abrechnung.abrechnungszeitraum_von} - {verguetung.letzte_abrechnung.abrechnungszeitraum_bis}
                  </p>
                  <p className="text-lg font-bold text-indigo-600">{verguetung.letzte_abrechnung.betrag?.toFixed(2) || '0.00'} €</p>
                  <Badge variant="secondary" className="mt-1">{verguetung.letzte_abrechnung.status}</Badge>
                </div>
              ) : (
                <p className="text-slate-400 text-sm">Keine Abrechnungen vorhanden</p>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Offene Anträge</p>
                  <p className="text-xl font-bold">{verguetung.offene_antraege || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Genehmigt (Jahr)</p>
                  <p className="text-xl font-bold">{verguetung.gesamt_betrag_jahr?.toFixed(2) || '0.00'} €</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Übergabe-Kachel - nur sichtbar wenn Status = uebergabe */}
      {stammdaten?.status === 'uebergabe' && (
        <Card
          className="border-orange-200 bg-orange-50 cursor-pointer hover:shadow-md transition-shadow print:break-inside-avoid"
          onClick={() => { openTab(`/akten/${id}/uebergabe`, { title: `Übergabe: ${stammdaten.vorname} ${stammdaten.nachname}` }); navigate(`/akten/${id}/uebergabe`); }}
          data-testid="uebergabe-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="w-6 h-6 text-orange-600" />
                <div>
                  <p className="font-semibold text-orange-800">Übergabe-Workflow aktiv</p>
                  <p className="text-sm text-orange-600 mt-0.5">
                    Klicken um Checkliste und Fortschritt zu öffnen
                    {stammdaten.uebergabe_an && ` | Nachfolger: ${stammdaten.uebergabe_an}`}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer für Druck */}
      <div className="hidden print:block text-center text-xs text-slate-400 border-t pt-4 mt-8">
        Erstellt am {new Date().toLocaleDateString('de-DE')} | Olympus Betreuungssoftware
      </div>

      {/* Stammdaten Edit Modal */}
      <StammdatenEditModal
        open={stammdatenEditOpen}
        onClose={() => setStammdatenEditOpen(false)}
        klient={stammdaten}
        klientId={id}
        onSaved={() => loadData()}
      />
    </div>
  );
}
